import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Text } from '@/components/ui/Text'
import { CanonicalAnalysisOutput, SummaryCard, SummarySlotId, summaryCardsOf } from '@digital-twins/shared-types'
import { SegmentationMapping } from '@/types/components/audiences/segmentation-mapping'
import { ChatInvitationSkeleton, QuotesChatInvitation } from './QuotesChatInvitation'
import type { StudyChatProfile } from './respondentProfileText'
import {
  renderSummaryCard,
  SummaryCardBase,
  summaryCardHasFigures,
  SummaryCardQuestions,
  SummaryCardRenderContext,
} from './summary-card-registry'

export interface SummaryTabProps {
  canonicalAnalysis?: CanonicalAnalysisOutput | null
  audienceProfiles?: Array<StudyChatProfile>
  audienceProfilesLoading?: boolean
  chatVisible?: boolean
  onOpenChat?: () => void
  respondentCount?: number
  onTwinNameClick?: (name: string) => void
  survey?: any
  responses?: Array<any>
  analysisColumns?: Array<string>
  segmentationMappings?: Array<SegmentationMapping> | null
  /**
   * Whether the tab draws the Insights column. Off (product call, 2026-09-23):
   * Featured takes its width. Presentation only -- analysis still writes the
   * `kpi` card -- so the default below is the one switch that brings it back.
   * The report page never passes it; tests pass `true` to keep the column
   * covered while it is off.
   */
  showsInsightsCard?: boolean
}

function cardForSlot(cards: Array<SummaryCard>, slot: SummarySlotId): SummaryCard | undefined {
  return cards.find((card) => card.slot === slot)
}

/**
 * Distinct source questions named on the card. A heading is a caption: one
 * question still captions the body; two or more would name the first read and
 * mislabel the rest (those bodies carry the question per row instead).
 */
function distinctSourceQuestionCount(card: SummaryCard | undefined): number {
  if (!card?.sourceQuestions) {
    return 0
  }
  // Counted by TEXT, not by id, because a heading is a caption and the heading
  // renderer already dedupes by text. The two legs of a lift pair are two ids
  // carrying one title ("Please rate each brand.") — one read measured twice,
  // which a single caption describes correctly. Counting ids would suppress
  // the heading on exactly the card that most needs one.
  const ids = new Set<string>()
  for (const source of card.sourceQuestions) {
    const text = (source.questionText ?? '').trim()
    if (text) {
      ids.add(text)
      continue
    }
    if (source.questionId) {
      ids.add(source.questionId)
    }
  }
  return ids.size
}

/**
 * Does this frame print the card's question above the body?
 *
 * Two independent reasons not to, and the card has to pass both. A card naming
 * more than one question gets no heading, because one caption would mislabel
 * the rest. And a card whose BODY already prints the question gets none
 * either: `question` and `fallback` are both drawn by `QuestionCard`, which
 * heads itself, so a frame heading printed the same sentence twice, once in
 * the frame's wording and once in the card's.
 */
function printsQuestionHeading(card: SummaryCard | undefined): boolean {
  if (!card) {
    return false
  }
  if (card.shape === 'question' || card.shape === 'fallback') {
    return false
  }
  return distinctSourceQuestionCount(card) <= 1
}

interface SlotFrameProps {
  slot: 'kpi' | 'plot' | 'quotes'
  title: string
  card: SummaryCard | undefined
  sentence: string
  context: SummaryCardRenderContext
  /**
   * Whether this frame prints the question heading above its body.
   *
   * Card chrome. `printsQuestionHeading` decides it; the frame only obeys.
   * The card stamps `sourceQuestions` either way, and the base line reads the
   * same flag to know whether the reads still need saying somewhere.
   */
  withQuestions?: boolean
  /**
   * Whether this frame is carrying a combined numeric panel (S3).
   *
   * Layout only, and set from the KPI card's own `presentation` disposition by
   * `SummaryTab`. Undefined on the frames the disposition says nothing about,
   * so only the plot frame states it.
   */
  combined?: boolean
  children?: ReactNode
  emptyContent?: ReactNode
}

function SlotFrame(props: SlotFrameProps) {
  const { slot, title, card, sentence, context, withQuestions = true, combined, children, emptyContent } = props
  // A card whose every figure is withheld (`summaryCardHasFigures`) is
  // unavailable here, so the slot states that rather than an empty body.
  const isAvailable =
    card?.status === 'available' && card.payload != null && summaryCardHasFigures(card, context)

  return (
    <div
      data-testid={`summary-slot-${slot}`}
      data-combined={combined === undefined ? undefined : String(combined)}
      // Border, NO fill. Every other report surface (the hero, `QuoteCards`,
      // `ReportQuoteCarousel`, `ReportAudienceSection`, `QuestionCard`) is
      // filled with `--report-card` and has no border; the summary slots are
      // the inverse. Carrying both made them a third style matching neither,
      // and the fill reads as a grey tint in light mode.
      className="rounded-2xl border border-border/60 px-5 py-5 flex flex-col gap-4 min-w-0"
    >
      <Text size="sm" className="font-medium text-muted-foreground">
        {title}
      </Text>
      {/*
        The question the card answers, above the figures and below the slot's
        own name. A card that names its read prints it, and one that does not
        prints nothing.

        (Supersedes "every slot, no shape branch": there is now one branch, in
        `printsQuestionHeading`, and it exists to print the question ONCE. The
        two shapes drawn by `QuestionCard` head themselves, so the frame that
        also headed them printed the same question twice.)
      */}
      {isAvailable && card && withQuestions && (
        <SummaryCardQuestions card={card} context={context} />
      )}
      {isAvailable ? (
        children
      ) : emptyContent ? (
        emptyContent
      ) : (
        <div
          data-testid={`summary-slot-empty-${slot}`}
          className="flex-1 flex items-center justify-center text-sm text-muted-foreground text-center px-2 py-8"
        >
          {sentence}
        </div>
      )}
      {/*
        How many respondents are behind the figures, under them, once per card.
        Frame chrome for the same reason the question is: four Featured shapes
        state figures and none of them carried a base, and the alternative was
        four private footers that would drift apart. The card decides whether it
        HAS a base (`summaryCardBaseSize`); the frame only places it.
      */}
      {isAvailable && card && (
        <SummaryCardBase card={card} context={context} withQuestions={withQuestions} />
      )}
    </div>
  )
}

/** Exactly the `t` `useTranslation('app')` hands back, so no cast is needed. */
type Translate = ReturnType<typeof useTranslation<'app'>>['t']

/**
 * Public copy stays generic. The analysis reason is retained on the card for
 * diagnostics, but exposing it here leaks producer and questionnaire internals.
 */
function slotUnavailableMessage(t: Translate): string {
  return t('studyReport.slotAbsent')
}

export function SummaryTab(props: SummaryTabProps) {
  const {
    canonicalAnalysis,
    audienceProfiles,
    audienceProfilesLoading = false,
    chatVisible = false,
    onOpenChat,
    respondentCount,
    onTwinNameClick,
    survey,
    responses,
    analysisColumns,
    segmentationMappings,
    showsInsightsCard = false,
  } = props
  const { t } = useTranslation('app')

  const cards = summaryCardsOf(canonicalAnalysis)
  const itemTags = canonicalAnalysis?.itemTags ?? {}
  const kpiCard = cardForSlot(cards, 'kpi')
  const plotCard = cardForSlot(cards, 'plot')
  const quotesCard = cardForSlot(cards, 'quotes')
  const chatInvitationEligible =
    (quotesCard?.status === 'absent' || quotesCard?.status === 'suppressed') &&
    chatVisible &&
    onOpenChat !== undefined
  const showChatInvitationSkeleton = chatInvitationEligible && audienceProfilesLoading
  const showChatInvitation =
    chatInvitationEligible &&
    !audienceProfilesLoading &&
    (audienceProfiles?.length ?? 0) > 0

  const context: SummaryCardRenderContext = {
    itemTags,
    survey,
    responses,
    analysisColumns,
    segmentationMappings,
    canonicalAnalysis,
    audienceProfiles,
    respondentCount,
    onTwinNameClick,
  }

  /**
   * One read, one area (S3, N4).
   *
   * With the Insights column off, Featured always takes its width. When it is
   * on, analysis decides: `combined_with_plot` means every read the Insights
   * column would state is one Featured already draws, so the two read as one
   * wider panel. Current analyses stamp it on an Insights card with no
   * payload; older runs stamped it on a column that restated the chart.
   * Nothing here compares payloads, values or sentences -- the tab chooses no
   * evidence (D1).
   *
   * THE PLOT CHECK IS DEFENSIVE. A disposition beside a plot that did not draw
   * would leave the reader with neither frame, so the page keeps its three
   * standalone frames then. With the Insights column off there is no third
   * frame to keep: an unavailable Featured shows its own empty state at the
   * combined width.
   */
  const isPlotDrawn =
    plotCard?.status === 'available' &&
    plotCard.payload != null &&
    summaryCardHasFigures(plotCard, context)
  const isCombined =
    !showsInsightsCard || (kpiCard?.presentation === 'combined_with_plot' && isPlotDrawn)

  return (
    <div
      // 1 : 1.5 : 1.5, the proportions the page shipped with. The plot slot
      // still gets the widest column, but not at the quotes column's expense:
      // a verbatim is the only thing here in a respondent's own words, and it
      // was the one paying for the plot's extra width.
      //
      // `items-start` so each slot sizes to its content. Stretching three
      // unrelated reads to one height pads the short ones with dead space.
      //
      // One column until `lg`. Three columns held their ratio all the way down,
      // so on a laptop half-screen or a tablet the narrowest of them was a
      // Featured chart a few characters wide; below the breakpoint the slots
      // stack in reading order instead.
      //
      // Combined (S3): the KPI column's width goes to the plot, so the two
      // areas that stated the same figure become one panel. The two columns
      // take the hero row's split above them (`ReportLandingPage`: the cover
      // image is exactly a third, the headline card the rest less the same
      // 16px gap), so both rows share their edges. Below `lg` the panel is
      // simply the plot, stacked above the quotes as before.
      className={`py-5 grid grid-cols-1 ${
        isCombined ? 'lg:grid-cols-[minmax(0,1fr)_calc(100%/3)]' : 'lg:grid-cols-[1fr_1.5fr_1.5fr]'
      } gap-4 items-start`}
      data-testid="summary-slots"
    >
      {!isCombined && (
        <SlotFrame
          slot="kpi"
          title={t('studyReport.slotKpi')}
          card={kpiCard}
          sentence={slotUnavailableMessage(t)}
          context={context}
          withQuestions={printsQuestionHeading(kpiCard)}
        >
          {kpiCard && renderSummaryCard(kpiCard, context)}
        </SlotFrame>
      )}

      <SlotFrame
        slot="plot"
        title={t('studyReport.slotPlot')}
        card={plotCard}
        sentence={slotUnavailableMessage(t)}
        context={context}
        withQuestions={printsQuestionHeading(plotCard)}
        combined={isCombined}
      >
        {plotCard && (
          <div data-testid={`summary-plot-${plotCard.shape}`} className="flex-1 min-w-0">
            {renderSummaryCard(plotCard, context)}
          </div>
        )}
      </SlotFrame>

      <SlotFrame
        slot="quotes"
        title={t(showChatInvitation || showChatInvitationSkeleton ? 'studyReport.emptyQuotesChatEyebrow' : 'studyReport.slotQuotes')}
        card={quotesCard}
        sentence={slotUnavailableMessage(t)}
        context={context}
        withQuestions={printsQuestionHeading(quotesCard)}
        emptyContent={showChatInvitationSkeleton ? (
          <ChatInvitationSkeleton />
        ) : showChatInvitation ? (
          <QuotesChatInvitation
            profiles={audienceProfiles ?? []}
            selectionSeed={canonicalAnalysis?.experimentId ?? ''}
            onOpenChat={onOpenChat}
          />
        ) : undefined}
      >
        {quotesCard && renderSummaryCard(quotesCard, context)}
      </SlotFrame>
    </div>
  )
}
